// Чистые помощники витрины «Наблюдаемость» (форматирование и раскладка).
//
// Раздел показывает измеренный факт — когда и где судно не давало позиций
// дольше собственной нормы, — и НЕ объясняет причину: гипотеза о спутниковом
// покрытии проверена и отвергнута (docs/plans/2026-08-02-ring-calibration-nogo.md).
// Форматирование это уважает: не изображаем точность, которой нет, и отличаем
// «ноль провалов» от «данных нет».

import { formatNumber, getLang, t } from '../i18n'

export interface HourBucket {
  hour: number
  count: number
}

/** Часы → строка. `null` (нет данных) → прочерк, а не «0 ч». */
export function formatHours(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  if (value === 0) return t('obs.hours', { value: '0' })
  // От сотни часов десятые доли — ложная точность: провал измерен с точностью
  // до минуты старта, а не до 6 секунд.
  if (Math.abs(value) >= 100) {
    return t('obs.hours', { value: formatNumber(getLang(), Math.round(value), 0) })
  }
  return t('obs.hours', { value: formatNumber(getLang(), value, 1) })
}

/** Минуты → «45 мин» / «2 ч 30 мин» / «1 сут 1 ч». `null` → прочерк. */
export function formatMinutes(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  const minutes = Math.round(value)
  if (minutes < 60) return t('obs.minutes', { value: minutes })
  if (minutes < 1440) {
    const h = Math.floor(minutes / 60)
    const m = minutes % 60
    return m
      ? `${t('obs.hours', { value: h })} ${t('obs.minutes', { value: m })}`
      : t('obs.hours', { value: h })
  }
  const days = Math.floor(minutes / 1440)
  const hours = Math.round((minutes % 1440) / 60)
  return hours
    ? `${t('obs.days', { value: days })} ${t('obs.hours', { value: hours })}`
    : t('obs.days', { value: days })
}

/**
 * Высота столбика в процентах от максимума.
 *
 * Ненулевое значение всегда получает видимую полоску: иначе редкие, но
 * содержательные ведра (провалы ≥7 суток) исчезали бы с графика, и он читался
 * бы как «таких не бывает».
 */
export function barShare(value: number, max: number): number {
  if (!max || max <= 0) return 0
  if (value <= 0) return 0
  return Math.max(1.5, (value / max) * 100)
}

/** Пресет периода: `days` суток назад от `now`. Обе границы — ISO UTC. */
export function periodPreset(days: number, now: Date = new Date()): {
  from: string
  to: string
} {
  const to = new Date(now.getTime())
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000)
  return { from: from.toISOString(), to: to.toISOString() }
}

/**
 * Часы суток с наибольшим числом провалов (не более `limit`).
 *
 * Ровный профиль (все значения равны, в т.ч. все нули) пиков НЕ даёт: назвать
 * пиком первый попавшийся час значило бы придумать суточную закономерность там,
 * где её нет.
 */
export function peakHours(profile: HourBucket[], limit: number): number[] {
  if (!profile.length) return []
  const max = Math.max(...profile.map(b => b.count))
  const min = Math.min(...profile.map(b => b.count))
  if (max <= 0 || max === min) return []
  return profile
    .filter(b => b.count === max)
    .slice(0, limit)
    .map(b => b.hour)
    .sort((a, b) => a - b)
}
